import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import InfoIcon from "@/assets/colorable/info_colorable_i.svg?react";
import EditIcon from "@/assets/colorable/pen_edit.svg?react";
import Loader from "@/components/Common/kpi/Loader";
import DisciplineMark from "@/components/discipline/DisciplineMark";
import type {PunishmentDetailSerializer, PunishmentSerializer} from "@/generated/Punishment";
import useCountdown from "@/hooks/useCountdown";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import type {TPunishmentType} from "@/store/types";
import {getDisciplineColor, getRemainingTime, isUnverifiedType} from "@/utils/disciplineUtils";
import {memo, useEffect, useId, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";

const TimerDisciplineMark: FC<{
  className?: string;
  discipline?: PunishmentSerializer | PunishmentDetailSerializer;
  instagramUnverificationId?: number;
  onClick?: () => void;
  tiktokUnverificationId?: number;
  type: TPunishmentType | "GENERAL_UNVERIFIED";
  ugcUnverificationId?: number;
  userId?: number;
}> = ({
  className,
  discipline,
  instagramUnverificationId,
  onClick,
  tiktokUnverificationId,
  type,
  ugcUnverificationId,
  userId,
}) => {
  const {getLabelByDisciplineType, invalidateQueries} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const isAdmin = useIsAdmin();

  const {remainingDays, remainingHours, remainingMinutes} = useMemo(
    () => getRemainingTime(discipline?.expiration_date),
    [discipline?.expiration_date],
  );

  const {countdown: minutesCountdown, isCountdownFinished} = useCountdown({
    // Add 2 extra minutes to the countdown to delay the refetch slightly, since the backend might not update immediately when time runs out.
    // We only apply this delay if less than 15 minutes remain, as it's unnecessary for longer durations.
    from: remainingHours === 0 && remainingMinutes < 15 ? remainingMinutes + 2 : remainingMinutes,
    interval: 60,
    shouldStartCountdown: remainingDays === 0,
  });

  useEffect(() => {
    if (isCountdownFinished && remainingHours === 0) {
      invalidateQueries();
    }
  }, [invalidateQueries, isCountdownFinished, remainingHours]);

  const id = useId();

  const punishmentQuery = useGenericQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishment(discipline?.id!),
    enabled: false,
  });

  const allPunishmentsQuery = useGenericInfiniteQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishments(userId!),
    enabled: !!userId && type === "GENERAL_UNVERIFIED",
  });

  const campaignApplication = useMemo(
    () =>
      typeof discipline?.campaign_application === "number"
        ? punishmentQuery.data?.campaign_application
        : discipline?.campaign_application,
    [punishmentQuery.data?.campaign_application, discipline?.campaign_application],
  );

  const getWhatIsSectionText = useMemo(() => {
    switch (type) {
      case "PERMANENT_BAN":
        return t("what_is_ban_text");
      case "PROBATION":
        return t("what_is_probation_text");
      case "TIMED_BAN":
        return t("what_is_timed_ban_text");
      default:
        return "";
    }
  }, [t, type]);

  const punishmentDetails = useMemo(() => {
    if (type === "GENERAL_UNVERIFIED") {
      const unverifications = allPunishmentsQuery.data?.filter(
        punishment =>
          punishment.id === instagramUnverificationId ||
          punishment.id === tiktokUnverificationId ||
          punishment.id === ugcUnverificationId,
      );

      return unverifications?.map(punishment => ({
        body: punishment.body,
        campaignTitle: punishment.campaign_application?.campaign?.title,
        id: punishment?.id,
        title: punishment.title,
        type: punishment.punishment_type,
      }));
    }

    return [
      {
        body: discipline?.body,
        campaignTitle: campaignApplication?.campaign.title,
        id: discipline?.id,
        title: discipline?.title,
        type: discipline?.punishment_type,
      },
    ];
  }, [
    allPunishmentsQuery.data,
    campaignApplication,
    discipline,
    getWhatIsSectionText,
    instagramUnverificationId,
    tiktokUnverificationId,
    type,
    ugcUnverificationId,
  ]);

  return (
    <button
      className={clsxm(
        "flex h-8 w-fit items-center gap-2 rounded-md px-3 text-white",
        className,
        !onClick && "cursor-default",
      )}
      onClick={e => {
        e.stopPropagation();
        onClick?.();
      }}
      style={{backgroundColor: `var(--color-${getDisciplineColor(type)})`}}>
      <DisciplineMark className="size-4" type={type} />
      {discipline?.expiration_date && remainingMinutes > 0 && (
        <span className="text-sm">
          {remainingDays > 0
            ? `${remainingDays}${t("days_abbrevation")}`
            : `${remainingHours.toString().padStart(2, "0")}:${minutesCountdown.toString().padStart(2, "0")}`}
        </span>
      )}
      {isAdmin && <EditIcon className="size-3.5" />}
      <div data-tooltip-id={`discipline-tooltip-${id}`}>
        <InfoIcon className="size-3.5" style={{color: `var(--color-${getDisciplineColor(type)})`}} />
      </div>
      <Tooltip
        className="border-neutral-white-dark z-100 border p-3 shadow-md"
        classNameArrow="shadow-md"
        clickable
        id={`discipline-tooltip-${id}`}
        opacity={1}
        delayShow={200}
        place="bottom"
        variant="light"
        afterShow={() => {
          if (typeof discipline?.campaign_application === "number" && !punishmentQuery.isFetched) {
            punishmentQuery.refetch();
          }
        }}>
        <div className="flex max-w-72 flex-col gap-2.5 bg-white">
          {punishmentQuery.isFetching && <Loader styles={{loaderClassName: "size-6 align-center"}} />}
          {punishmentDetails?.map(punishment => (
            <div className="flex flex-col gap-1" key={punishment.id}>
              <div className="flex items-center gap-2">
                <DisciplineMark type={punishment.type!} variant="outline" className="size-3" />
                <span className="text-neutral-black text-sm font-semibold">{punishment?.title}</span>
              </div>
              <span className="text-neutral-black text-left text-xs">{punishment.campaignTitle}</span>
              <div className="flex max-h-20 overflow-y-auto">
                <span className="text-neutral-grey-dark text-left text-[10px]">{punishment?.body}</span>
              </div>
              {!isUnverifiedType(type as TPunishmentType) && type !== "GENERAL_UNVERIFIED" && (
                <>
                  <div className="bg-neutral-grey-light-dark my-1.5 h-[1px] w-full" />
                  <span className="text-left text-[10px] font-semibold">
                    {t("what_is", {punishment: getLabelByDisciplineType(type as TPunishmentType).toLowerCase()})}
                  </span>
                  <span className="text-neutral-grey-dark text-left text-[10px]">{getWhatIsSectionText}</span>
                </>
              )}
            </div>
          ))}
        </div>
      </Tooltip>
    </button>
  );
};

export default memo(TimerDisciplineMark);
